import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { TitheService } from "services/tithe.service";
import { FundService } from "services/fund.service";
import { ISaveTitheDTO, IFundResponse } from "models/church.types";

export default function TitheRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [funds, setFunds] = useState<IFundResponse[]>([]);
  const [errors, setErrors] = useState<{ amount?: string }>({});

  // Initialisation conforme à l'interface ISaveTitheDTO + fundId
  const [tithe, setTithe] = useState<ISaveTitheDTO & { fundId?: number | null }>({
    amount: 0,
    titheDate: new Date().toISOString(),
    description: "",
    fundId: null,
  });

  useEffect(() => {
    const initData = async () => {
      setLoadingFunds(true);
      try {
        // 1. Chargement des caisses disponibles
        const fundsResult = await FundService.getAll(0, 100);
        setFunds(fundsResult.data || []);

        // 2. Si mode édition, chargement des détails de la dîme
        if (isEditMode && id) {
          const data = await TitheService.getById(parseInt(id, 10));
          setTithe({
            amount: data.amount,
            titheDate: data.titheDate,
            description: data.description || "",
            fundId: data.fundId ?? null,
          });
        }
      } catch (error) {
        toast.error("Erreur lors du chargement des données");
        if (isEditMode) {
          history.push("/admin/tithes");
        }
      } finally {
        setLoadingFunds(false);
      }
    };
    initData();
  }, [id, isEditMode, history]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: { amount?: string } = {};
    if (tithe.amount <= 0) {
      newErrors.amount = "Le montant doit être supérieur à 0";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        // Envoi de la mise à jour avec l'ID via le DTO UpdateTitheDTO
        await TitheService.update({ ...tithe, id: parseInt(id, 10) });
        toast.success("Dîme mise à jour avec succès");
      } else {
        await TitheService.create(tithe);
        toast.success("Dîme enregistrée avec succès");
      }
      history.push("/admin/tithes");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center mt-12 p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="bg-[#1E293B] border border-slate-700 shadow-2xl rounded-xl overflow-hidden">
          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700 flex justify-between items-center">
            <h6 className="text-white text-sm font-bold uppercase tracking-widest">
              <i className="fas fa-donate mr-2 text-[#D4AF37]"></i>
              {isEditMode ? "Éditer la dîme" : "Nouvelle dîme"}
            </h6>
          </div>

          <form
            onSubmit={handleSubmit}
            className="px-6 py-8 grid grid-cols-1 gap-6"
          >
            {/* Montant */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Montant
              </label>
              <input
                type="number"
                value={tithe.amount === 0 ? "" : tithe.amount}
                onChange={(e) => {
                  const val = e.target.value;
                  setTithe({
                    ...tithe,
                    amount: val === "" ? 0 : Number(val),
                  });
                  if (errors.amount) setErrors({});
                }}
                placeholder="0"
                className={`w-full bg-[#0F172A] border ${
                  errors.amount ? "border-red-500" : "border-slate-700"
                } rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all`}
              />
              {errors.amount && (
                <span className="text-red-500 text-xs mt-1 ml-1 block">
                  {errors.amount}
                </span>
              )}
            </div>

            {}
            {/* Caisse destination */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                <i className="fas fa-wallet mr-2 text-[#D4AF37]"></i>Caisse destination
              </label>
              <div className="relative">
                <select
                  name="fundId"
                  value={tithe.fundId ?? ""}
                  onChange={(e) =>
                    setTithe({
                      ...tithe,
                      fundId: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  disabled={loadingFunds}
                  className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all appearance-none cursor-pointer disabled:opacity-50"
                >
                  <option value="">-- Aucune caisse (Non rattachée) --</option>
                  {funds.map((fund) => (
                    <option key={fund.id} value={fund.id}>
                      {fund.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                  {loadingFunds ? (
                    <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
                  ) : (
                    <i className="fas fa-chevron-down text-xs"></i>
                  )}
                </div>
              </div>
              <p className="text-slate-500 text-[11px] mt-1 italic ml-1">
                Le montant versé sera crédité sur cette caisse.
              </p>
            </div>

            {/* Date de versement */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Date du versement
              </label>
              <input
                type="datetime-local"
                value={tithe.titheDate ? tithe.titheDate.slice(0, 16) : ""}
                onChange={(e) =>
                  setTithe({
                    ...tithe,
                    titheDate: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : new Date().toISOString(),
                  })
                }
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none transition-all [color-scheme:dark]"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-[10px] text-[#D4AF37] uppercase font-black ml-1 mb-2 block">
                Description / Notes
              </label>
              <textarea
                value={tithe.description || ""}
                onChange={(e) =>
                  setTithe({ ...tithe, description: e.target.value })
                }
                placeholder="Informations complémentaires ou référence du donateur..."
                className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none h-24 transition-all resize-none"
              />
            </div>

            {/* Boutons d'action */}
            <div className="mt-6 flex justify-end gap-4 border-t border-slate-700 pt-6">
              <button
                type="button"
                onClick={() => history.push("/admin/tithes")}
                className="px-6 py-3 text-slate-400 hover:text-white font-bold text-xs uppercase transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase hover:scale-105 active:scale-95 transition-all shadow-md disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner animate-spin mr-2"></i> Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i> Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}